import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useZones, useCreateZone, useUpdateZone, useDeleteZone } from '@/hooks/useZones';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Pencil, Trash2, Plus, Save, X, MapPin } from 'lucide-react';
import type { Zone } from '@/types/entities';

interface ZonesSectionProps {
  restaurantId: string;
}

export function ZonesSection({ restaurantId }: ZonesSectionProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const { data: zones, isLoading } = useZones(restaurantId);
  const createZone = useCreateZone(restaurantId);
  const updateZone = useUpdateZone(restaurantId);
  const deleteZone = useDeleteZone(restaurantId);

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    nameAr: '',
    nameFr: '',
    isActive: true,
  });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  const resetForm = () => {
    setForm({
      nameAr: '',
      nameFr: '',
      isActive: true,
    });
  };

  const handleAdd = async () => {
    // Require at least one language
    if (!form.nameAr && !form.nameFr) return;

    // Auto-fill missing language with the other
    const nameAr = form.nameAr || form.nameFr;
    const nameFr = form.nameFr || form.nameAr;

    await createZone.mutateAsync({
      nameAr,
      nameFr,
    } as { nameAr: string; nameFr: string });

    resetForm();
    setIsAdding(false);
  };

  const handleEdit = (zone: Zone) => {
    setEditingId(zone.id);
    setForm({
      nameAr: zone.nameAr,
      nameFr: zone.nameFr,
      isActive: zone.isActive,
    });
  };

  const handleSave = async (zoneId: string) => {
    // Require at least one language
    if (!form.nameAr && !form.nameFr) return;

    // Auto-fill missing language with the other
    const nameAr = form.nameAr || form.nameFr;
    const nameFr = form.nameFr || form.nameAr;

    await updateZone.mutateAsync({
      zoneId,
      nameAr,
      nameFr,
      isActive: form.isActive,
    } as { zoneId: string; nameAr: string; nameFr: string; isActive: boolean });

    resetForm();
    setEditingId(null);
  };

  const handleCancel = () => {
    resetForm();
    setEditingId(null);
    setIsAdding(false);
  };

  const handleDelete = (zoneId: string) => {
    setConfirmDialog({
      open: true,
      title: t('settings.confirmDeleteZone') || 'Delete Zone',
      description:
        t('settings.confirmDeleteZoneDescription') ||
        'Are you sure you want to delete this zone? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteZone.mutateAsync(zoneId);
        } catch (error: any) {
          if (error?.message === 'ZONE_IN_USE') {
            setAlertDialog({
              open: true,
              title: t('settings.zoneInUseTitle') || 'Zone In Use',
              description:
                t('settings.zoneInUseDescription') ||
                'This zone cannot be deleted because it is still being used. Please remove all references first.',
            });
          } else {
            setAlertDialog({
              open: true,
              title: t('common.error') || 'Error',
              description:
                error?.message ||
                t('settings.deleteZoneError') ||
                'Failed to delete zone. Please try again.',
            });
          }
        }
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('settings.zones') || 'Zones'}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('settings.zonesDescription') || 'Manage zones for table and staff assignment'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)} disabled={isAdding}>
            <Plus className="w-4 h-4 me-2" />
            <span className="truncate">{t('settings.addZone') || 'Add Zone'}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {/* Add Form */}
            {isAdding && (
              <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
                      <Plus className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg">{t('settings.addZone') || 'Add Zone'}</h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>
                        {t('settings.zoneNameAr') || 'Name (Arabic)'}
                        {!form.nameFr && <span className="text-destructive"> *</span>}
                      </Label>
                      <Input
                        value={form.nameAr}
                        onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                        placeholder="الطابق الأول"
                        dir="rtl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t('settings.zoneNameFr') || 'Name (French)'}
                        {!form.nameAr && <span className="text-destructive"> *</span>}
                      </Label>
                      <Input
                        value={form.nameFr}
                        onChange={(e) => setForm({ ...form, nameFr: e.target.value })}
                        placeholder="Premier étage"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('settings.oneLanguageRequired') || 'At least one language is required'}
                  </p>
                  <div className="flex gap-2 justify-end mt-4">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={createZone.isPending}
                    >
                      <X className="w-4 h-4 me-2" />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleAdd}
                      disabled={createZone.isPending || (!form.nameAr && !form.nameFr)}
                    >
                      <Save className="w-4 h-4 sm:me-2" />
                      <span className="hidden sm:inline">{t('common.save')}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Zones List */}
            {!zones || zones.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                    <MapPin className="w-8 h-8 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground">
                  {t('settings.noZones') || 'No zones yet. Add your first zone above.'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {zones.map((zone) => (
                  <Card key={zone.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      {editingId === zone.id ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>
                              {t('settings.zoneNameAr') || 'Name (Arabic)'}
                              {!form.nameFr && <span className="text-destructive"> *</span>}
                            </Label>
                            <Input
                              value={form.nameAr}
                              onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                              dir="rtl"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>
                              {t('settings.zoneNameFr') || 'Name (French)'}
                              {!form.nameAr && <span className="text-destructive"> *</span>}
                            </Label>
                            <Input
                              value={form.nameFr}
                              onChange={(e) => setForm({ ...form, nameFr: e.target.value })}
                            />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.oneLanguageRequired') ||
                              'At least one language is required'}
                          </p>
                          <div className="flex items-center justify-between">
                            <Label>{t('settings.zoneActive') || 'Active'}</Label>
                            <Switch
                              checked={form.isActive}
                              onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(zone.id)}
                              disabled={updateZone.isPending || (!form.nameAr && !form.nameFr)}
                              className="flex-1"
                            >
                              <Save className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.save')}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancel}
                              disabled={updateZone.isPending}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                                <MapPin className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <div className="font-semibold">
                                  {isArabic ? zone.nameAr : zone.nameFr}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {isArabic ? zone.nameFr : zone.nameAr}
                                </div>
                              </div>
                            </div>
                            {!zone.isActive && (
                              <span className="text-xs bg-muted px-2 py-1 rounded">
                                {t('common.inactive')}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(zone)}
                              className="flex-1"
                            >
                              <Pencil className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.edit')}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(zone.id)}
                              disabled={deleteZone.isPending}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Confirm Dialog */}
      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="destructive"
      />

      {/* Alert Dialog */}
      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
        actionLabel={t('common.ok') || 'OK'}
      />
    </Card>
  );
}








